import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import {
    noActiveLinkColour,
    getBackendBaseUrl,
    ACCESS_TOKEN,
    REFRESH_TOKEN_API,
    isLocalEnvironment
} from "../constants";
import { store } from "../store";
import i18next from "i18next";
import { showToast } from "./DesignSystem/Toast";
import {
    storeBatchLinks,
    storeBatchShapes,
    setCurrentBatch,
    toggleBatchSelection
} from "../actions/regulation/BatchSelection";
import AES from "crypto-js/aes";
import Utf8 from "crypto-js/enc-utf8";
import { isPathPublicViewer } from "../Utilities/generalUtilities";
import { logout, currentApplicationVersion } from "actions";
import { toPng } from "html-to-image";
import { identityExchange } from "./authentication/useLogin";
import { postMessageToParent } from "./authentication/useIdentityExchange";

let controller = [];

const closeIcon =
    '<svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19"><defs><style>.a{fill:#7c7c7c;}</style></defs><path class="a" d="M16.217,2.783A9.5,9.5,0,1,0,2.783,16.217,9.5,9.5,0,1,0,16.217,2.783Zm-.764,12.671A8.42,8.42,0,0,1,3.547,3.547,8.42,8.42,0,1,1,15.454,15.454Z" transform="translate(0 0)"/><path class="a" d="M137.458,129.084a.538.538,0,0,0-.764,0l-3.423,3.423-3.423-3.423a.54.54,0,1,0-.764.764l3.423,3.423-3.423,3.423a.542.542,0,0,0,.38.924.527.527,0,0,0,.38-.16l3.423-3.423,3.423,3.423a.543.543,0,0,0,.38.16.52.52,0,0,0,.38-.16.538.538,0,0,0,0-.764l-3.415-3.423,3.423-3.423A.538.538,0,0,0,137.458,129.084Z" transform="translate(-123.771 -123.771)"/></svg>';

export const getRequest = function (url, queryParameters = {}) {
    const queryParametersLength = Object.keys(queryParameters).length;

    if (queryParametersLength > 0) {
        let queryParameter = "";

        Object.keys(queryParameters).forEach((parameter, index) => {
            if (!(parameter === "fields" && queryParameters.fields === "all")) {
                if (index !== queryParametersLength - 1) {
                    queryParameter += `${parameter}=${
                        typeof queryParameters[parameter] !== "string"
                            ? JSON.stringify(queryParameters[parameter])
                            : queryParameters[parameter]
                    }&`;
                } else {
                    queryParameter += `${parameter}=${
                        typeof queryParameters[parameter] !== "string"
                            ? JSON.stringify(queryParameters[parameter])
                            : queryParameters[parameter]
                    }`;
                }
            }
        });
        if (queryParameter !== "") {
            queryParameter = `?${queryParameter}`;
        }

        return axios.get(url + queryParameter).then((response) => response.data);
    }

    return axios.get(url).then((response) => response.data);
};

export const getAxiosInstance = () => {
    const axiosInstance = axios.create({
        baseURL: ""
    });

    axiosInstance.defaults.headers.common = {};
    axiosInstance.defaults.withCredentials = false;
    axiosInstance.interceptors.request.use((req) => {
        if (hasSessionTimedOut(req)) {
            return Promise.reject("Session timed out");
        }
        return req;
    });
    return axiosInstance;
};

export const hasSessionTimedOut = (req) => {
    if (isLocalEnvironment) return false;

    // Skip idle-session timeout for iframe views — the user's session is
    // managed by the parent app (Hotspot). If the token expires, the 401
    const isIframeView = store.getState().city.isIframeView === true;
    if (isIframeView) return false;

    if (
        !req.url.includes("logout") &&
        localStorage.getItem("isUserAuthenticated") === "true" &&
        !isPathPublicViewer() &&
        !(window.location.pathname === "/")
    ) {
        const currentTime = Date.now();
        const lastAPICall = localStorage.getItem("lastAPICall");
        const sessionExpiresIn =
            store
                .getState()
                .city.cityConfig.find(
                    (config) => config.config_key.toLowerCase() === "idle_session_timeout"
                )?.config_value * 1000;
        if (sessionExpiresIn && currentTime - lastAPICall >= sessionExpiresIn) {
            alert(i18next.t("You will be logged out due to inactivity. Please login again"));
            localStorage.setItem("lastAPICall", Date.now());
            store.dispatch(logout());
            return true;
        }
        localStorage.setItem("lastAPICall", Date.now());
    }
    if (isPathPublicViewer() || window.location.pathname === "/") {
        localStorage.setItem("lastAPICall", Date.now());
    }
    return false;
};

export const setAxiosDefaultParams = function () {
    axios.defaults.withCredentials = true;
    axios.defaults.xsrfHeaderName = "X-CSRFTOKEN";
    axios.defaults.xsrfCookieName =
        process.env.REACT_APP_ENVIRONMENT?.toLowerCase() === "staging"
            ? "staging-csrftoken"
            : "prod-csrftoken";

    axios.defaults.headers.patch["Content-Type"] = "application/json; charset=utf-8";

    if (!axios.defaults.baseURL) {
        axios.defaults.baseURL = getBackendBaseUrl();
    }

    const csrftokenElement = document.querySelector("[name=csrfmiddlewaretoken]");
    const csrftoken = csrftokenElement ? csrftokenElement.value : null;

    if (csrftoken) {
        axios.defaults.headers.post["X-CSRFTOKEN"] = csrftoken;
        axios.defaults.headers.put["X-CSRFTOKEN"] = csrftoken;
        axios.defaults.headers.patch["X-CSRFTOKEN"] = csrftoken;
        axios.defaults.headers.delete["X-CSRFTOKEN"] = csrftoken;
    }

    if (csrftokenElement) {
        csrftokenElement.remove();
    }

    if (process.env.REACT_APP_ENVIRONMENT === "LOCAL") return;

    let isRefreshing = false;
    let failedRequests = [];

    axios.interceptors.response.use(
        (res) => {
            if (localStorage.getItem("version") !== res.headers["x-application-version"]) {
               store?.dispatch(
                    currentApplicationVersion({
                        currentApplicationVersion: res.headers["x-application-version"]
                    })
                );
            }
            return res;
        },
        async (err) => {
            const originalRequest = err.config;

            if (!err.response || originalRequest._retry) {
                return Promise.reject(err);
            }

            // Only intercept 401s
            if (err.response.status !== 401) {
                return Promise.reject(err);
            }

            // If the failing request is the refresh-token call itself, do not attempt
            // another refresh. Clear auth state and force logout to avoid recursion.
            if (originalRequest?.url && originalRequest.url.includes(REFRESH_TOKEN_API)) {
                localStorage.removeItem(ACCESS_TOKEN);
                failedRequests = [];
                isRefreshing = false;

                const isIframeViewForGuard = store.getState().city.isIframeView === true;
                if (isIframeViewForGuard) {
                    // Notify the parent and reload — useIdentityExchange will attempt
                    // identity exchange again; if it fails, Main.tsx renders AuthorizationFailed.
                    postMessageToParent({ type: "AUTH_REQUIRED", errorCode: 401 });
                    if (typeof window !== "undefined" && window.location) {
                        window.location.reload();
                    }
                } else {
                    try {
                        store?.dispatch(logout());
                    } catch (e) {
                        // Swallow dispatch errors; we'll still reload the page if possible.
                    }
                    if (typeof window !== "undefined" && window.location) {
                        window.location.reload();
                    }
                }
                return Promise.reject(err);
            }

            /*
             * Iframe  → calls identityExchange() (cookie-based SSO with Hotspot).
             * Direct  → calls POST /api/token/refresh/ (standard JWT refresh).
             *
             * Both paths share the same retry / queue logic:
             *   1. First 401 triggers the refresh; concurrent 401s are queued.
             *   2. On success: store new token, retry original + queued requests.
             *   3. On failure: clear token, reject everything.
             */
            const isIframeView = store.getState().city.isIframeView === true;

            if (!isRefreshing) {
                isRefreshing = true;

                try {
                    let newToken = null;

                    if (isIframeView) {
                        const response = await identityExchange();

                        if (response.accessToken) {
                            newToken = response.accessToken;
                        } else {
                            // Identity exchange succeeded but returned no token —
                            // notify the parent app so it can prompt re-auth.
                            postMessageToParent({
                                type: "AUTH_REQUIRED",
                                errorCode: response.errorCode
                            });
                        }
                    } else {
                        const res = await axios.post(
                            REFRESH_TOKEN_API,
                            {},
                            { withCredentials: true }
                        );
                        if (res.data?.access) {
                            newToken = res.data.access;
                        }
                    }

                    if (newToken) {
                        localStorage.setItem(ACCESS_TOKEN, newToken);
                        localStorage.setItem("lastAPICall", JSON.stringify(Date.now()));

                        originalRequest._retry = true;
                        originalRequest.headers.Authorization = "Bearer " + newToken;

                        failedRequests.forEach((cb) => cb());
                        failedRequests = [];

                        return axios(originalRequest);
                    }

                    // Refresh returned no usable token
                    localStorage.removeItem(ACCESS_TOKEN);
                    failedRequests = [];
                    return Promise.reject(err);
                } catch (refreshError) {
                    localStorage.removeItem(ACCESS_TOKEN);
                    failedRequests = [];
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }

            // Once the refresh completes, each queued callback reads the fresh
            // token from localStorage and retries.
            return new Promise((resolve, reject) => {
                failedRequests.push(() => {
                    const token = localStorage.getItem(ACCESS_TOKEN);
                    if (!token) {
                        reject(err);
                        return;
                    }

                    originalRequest._retry = true;
                    originalRequest.headers.Authorization = "Bearer " + token;
                    resolve(axios(originalRequest));
                });
            });
        }
    );

    axios.interceptors.request.use((req) => {
        if (hasSessionTimedOut(req)) {
            return Promise.reject("Session timed out");
        }

        if (
            (req.url === "/api/demand-chart-data/" ||
                req.url === "/api/demand-chart-aggregate-data/") &&
            controller.length &&
            req.controller
        ) {
            controller[0].abort("Cancelling Previous Request");
            controller.pop();
        }

        if (req.controller) {
            controller.push(req.controller);
        }

        if (!isPathPublicViewer() && window.location.pathname !== "/") {
            const token = localStorage.getItem(ACCESS_TOKEN);
            if (token) {
                req.headers.Authorization = "Bearer " + token;
            }
        }

        return req;
    });
};

export const showPopUp = function (
    text,
    title,
    cancelAction,
    type = "info",
    confirmAction = () => {},
    showCloseButton = true
) {
    const data = {
        title: title || i18next.t("Information"),
        html: text,
        showCloseButton: showCloseButton,
        closeButtonHtml: closeIcon,
        showCancelButton: cancelAction,
        showConfirmButton: confirmAction,
        allowOutsideClick: false,
        allowEnterKey: false,
        type,
        heightAuto: false
    };

    return Swal.fire(data).then((result) => {
        if (result.isConfirmed) {
            confirmAction && confirmAction();
        } else if (result.isDismissed) {
            cancelAction && cancelAction();
        } else if (result.value) {
            confirmAction && confirmAction();
        }
    });
};
export const managerPopUp = function (
    header1,
    header2,
    conflictText,
    note,
    impNote,
    action,
    type = "info"
) {
    const data = {
        html: `<span><h3 style="text-align:left; margin-top: -6px;color:#E04F5F;
        font: normal normal 600 15px/38px Roboto;
        letter-spacing: 0.3px; margin:0;">${i18next.t(header1)}</h3>


        <p style="text-align:left;font: normal normal 600 15px/26px Roboto;
        letter-spacing: 0.3px; margin:0;
        color: #272727">${i18next.t(conflictText)}</p></span>


        <p style="text-align:left; font: normal normal normal 14px/23px Roboto;
        letter-spacing: 0.28px;
        color: #727781; margin:0;">${i18next.t(note)}</p>

        <span><h3 style="text-align:left;margin:0px; font: normal normal 600 15px/38px Roboto;
        letter-spacing: 0.3px;
        color: #5076BA;">${i18next.t(header2)}</h3>

        <p style="text-align:left;font: normal normal normal 14px/23px Roboto;
        letter-spacing: 0.28px;
        color: #263757; margin:0;
        opacity: 1">${i18next.t(impNote)}</p></span>`,

        confirmButtonColor: "#5076BA",
        type,
        heightAuto: false
    };
    if (action) {
        return Swal.fire({ data }).then((result) => {
            if (result.value) {
                action();
            }
        });
    }
    return Swal.fire(data);
};
export const timedErrorPopUp = (text, title, timer = 1000) => {
    const data = {
        title: title || i18next.t("Error"),
        html: text,
        showCloseButton: true,
        heightAuto: false,
        timer,
        type: "error"
    };
    return Swal.fire(data);
};

const customIcons = {
    info: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="12" fill="#30B59F"/><path d="M6 12L10 16L18 8" stroke="white" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>'
};

export const showError = function (error) {
    console.error(error);
    if (!window.isOnline) {
        showToast(
            i18next.t(
                "Network connection is unavailable. Please check your internet connection and try again."
            ),
            { toastId: "network-toast", type: "warning" }
        );
        return;
    }
    const errorMessages = {
        errorMessageForTimeout: {
            message: i18next.t("Request timed out. Please try again."),
            toastId: "TIMEOUT_ERROR"
        },
        errorMessage: {
            message: i18next.t("An error occurred. Please try again"),
            toastId: "GENERIC_ERROR"
        },
        errorMessageForRequest: {
            message: i18next.t("An error occurred while processing your request. Please try again"),
            toastId: "REQUEST_ERROR"
        },
        errorMessageForPermissionDenied: {
            message: i18next.t(
                "You do not have the necessary permissions to access this resource."
            ),
            toastId: "PERMISSION_DENIED"
        },
        errorMessageForServerError: {
            message: i18next.t("Internal server error. Please try again later."),
            toastId: "SERVER_ERROR"
        }
    };
    const statusCodesToCheck = [400, 404, 409];
    if (error === "Session timed out") {
        showToast(error, {
            toastId: "SESSION_TIMEOUT",
            type: "error"
        });
    } else if (error.response) {
        let err = "";

        if (error.response.data.detail) {
            if (error.response.data.detail === "Authentication credentials were not provided.") {
                if (!window.location.href.includes("loginpage")) {
                    const isIframeView = store.getState().city.isIframeView === true;
                    localStorage.setItem("isUserAuthenticated", false);
                    if (isIframeView) {
                        // In iframe: notify parent and reload — useIdentityExchange
                        // will re-attempt auth; if it fails, AuthorizationFailed is shown.
                        postMessageToParent({ type: "AUTH_REQUIRED", errorCode: 401 });
                        window.location.reload();
                    } else {
                        window.location.reload();
                    }
                }
            }
            err = error.response.data.detail;
            if (error.response.status === 403) {
                err = errorMessages.errorMessageForPermissionDenied;
            }
        } else if (error.response.data?.name?.length) {
            err = error.response.data.name[0];
        } else if (statusCodesToCheck.includes(error.response.status)) {
            err = errorMessages.errorMessageForRequest;
        } else if (error.response.status === 500) {
            err = errorMessages.errorMessageForServerError;
        } else if (error.response.status === 504) {
            err = errorMessages.errorMessageForTimeout;
        } else {
            err = errorMessages.errorMessage;
        }
        showToast(err.message ? err.message : err, {
            toastId: `${err.toastId || err}`,
            type: "error"
        });
    } else if (error.request) {
        if (error.message === "Network Error") {
            showToast(errorMessages.errorMessageForRequest.message, {
                toastId: errorMessages.errorMessageForRequest.toastId,
                type: "error"
            });
        } else {
            showToast(error.request, {
                toastId: error.request,
                type: "error"
            });
        }
    } else {
        showToast(errorMessages.errorMessage.message, {
            toastId: errorMessages.errorMessage.toastId,
            type: "error"
        });
    }
};

export const hexToRGBArray = (h = noActiveLinkColour) => {
    let b = 0;
    let g = 0;
    let r = 0;

    // 3 digits
    if (h.length === 4) {
        r = `0x${h[1]}${h[1]}`;
        g = `0x${h[2]}${h[2]}`;
        b = `0x${h[3]}${h[3]}`;

        // 6 digits
    } else if (h.length === 7) {
        r = `0x${h[1]}${h[2]}`;
        g = `0x${h[3]}${h[4]}`;
        b = `0x${h[5]}${h[6]}`;
    }

    return [Number(r), Number(g), Number(b)];

    // Return "rgb(" + +r + "," + +g + "," + +b + ")";
};

export const getHeatmapColors = (colorConfigValues) => {
    const defaultColorScale = [
        { value: 0, color: "rgba(33,102,172,0)" }, // Transparent (no intensity)
        { value: 0.2, color: "rgb(103,169,207)" },
        { value: 0.4, color: "rgb(209,229,240)" },
        { value: 0.6, color: "rgb(253,219,199)" },
        { value: 0.8, color: "rgb(239,138,98)" },
        { value: 1, color: "rgb(178,24,43)" } // Maximum density (100% occupancy)
    ];
    if (!colorConfigValues.length) {
        return defaultColorScale;
    }
    const colors = colorConfigValues[0].config_value.colorSets[0]["colors"];
    const colorValues = Object.entries(colors).map(([key, color]) => {
        const [start, end] = key.split("-").map((k) => parseFloat(k) / 100);
        return { value: end, color };
    });
    colorValues.unshift({ value: 0, color: "rgba(33,102,172,0)" });
    return colorValues;
};

export const parseDateToPayload = (date, formatType = "YYYY-MM-DD") =>
    moment(date).format(formatType);

export const parseTimeToPayload = (time) => moment(time).format("HH:mm");

export const Polygon = "Polygon";

export const isOpenInHotspot = !!window.self?.location.href.toLowerCase().includes("is_hotspot=1");

export const removeBatchLayers = (dispatch) => {
    dispatch(toggleBatchSelection({ isEnabled: false }));
    dispatch(setCurrentBatch({ currentBatch: null }));
    dispatch(storeBatchShapes({ batchShapes: {} }));
    dispatch(storeBatchLinks({ batchLinks: {} }));
};

export const isSegmentPopup = (conditions) => {
    return conditions.every((condition) => condition);
};

export const handleDatePicker = () => {
    const i = document.querySelector(".react-datepicker__time");
    const j = document.querySelector(".react-datepicker-popper");
    /* eslint-disable-next-line no-unused-expressions */
    i?.setAttribute("hidden", "true");
    /* eslint-disable-next-line no-unused-expressions */
    j?.setAttribute("hidden", "true");
};

export function getPdf(pdfUrl) {
    try {
        axios
            .get(pdfUrl, {
                responseType: "blob"
            })
            .then((res) => {
                const blob = new Blob([res.data], { type: "application/pdf" });

                window.open(URL.createObjectURL(blob), "_blank");
            });
    } catch (error) {
        showError(error);
    }
}

export function formatNumberToVisxAxis(number) {
    if (number >= 1000000) {
        return `${(number / 1000000).toFixed(1)}m`;
    }
    if (number >= 1000) {
        return `${(number / 1000).toFixed(1)}k`;
    }

    return number.toString();
}

export const fetchAndCacheData = async (endpoint, params) => {
    const json = await axios
        .get(endpoint, params)
        .then((response) => {
            return response.data;
        })
        .then((data) => {
            let responseData = data;
            caches.open("curbiq-miscellaneous").then((cache) => {
                responseData = {
                    data,
                    time: new Date().getTime()
                };
                const responseHeaders = new Headers({
                    "Content-Type": "application/json"
                });
                const encryptedData = AES.encrypt(
                    JSON.stringify(responseData),
                    "sensitive-secret-key"
                ).toString();
                cache.put(
                    endpoint,
                    new Response(JSON.stringify(encryptedData), {
                        headers: responseHeaders
                    })
                );
            });

            return responseData;
        });
    return json;
};

export const retrieveCacheData = async (url, params = {}) => {
    const json = await caches
        .match(url)
        .then(async (cachedResponse) => {
            if (cachedResponse) {
                const cachedData = await cachedResponse.json();
                const currDate = new Date().getTime();
                const decryptedBytes = AES.decrypt(cachedData, "sensitive-secret-key");
                const decryptedData = JSON.parse(decryptedBytes.toString(Utf8));
                const diff = currDate - decryptedData.time;

                // Cache the api response for 5 minutes(5*60*1000 milliseconds)
                // If cache is fresh then return it
                if (diff < 300000) {
                    return decryptedData.data;
                } else {
                    const res = await fetchAndCacheData(url, params);
                    return res;
                }
            } else {
                // If api response is not cached, then fetch it
                const res = await fetchAndCacheData(url, params);
                return res;
            }
        })
        .then((data) => {
            return data;
        })
        .catch((error) => {
            showError(error);
        });
    return json;
};

export const getStatusStyle = (statusValue) => {
    switch (statusValue) {
        case "To Do":
            return "info";
        case "In Progress":
            return "warning";
        case "Complete":
            return "success";
        // Add more cases as needed
        default:
            return {}; // Default style if no match
    }
};

/**
 * Rounds a given value to the specified number of decimal places.
 */
export function round(value, decimals = 2) {
    // Check if the value is a valid number or a string that can be converted to a number
    if (isNaN(Number(value))) {
        return value;
        // throw new Error("Invalid input: value must be a number or a string representing a number.");
    }

    // Convert the value to a number
    const num = parseFloat(value);

    // Round the number to the specified number of decimal places
    const factor = Math.pow(10, decimals);
    return Math.round(num * factor) / factor;
}

export const getDashboardSnapShots = async () => {
    const snapShots = {};
    const parentContainer = document.querySelector(".hidden-layer");
    const chartContainers = parentContainer?.querySelectorAll(".chart-container") || [];

    for (const chartContainer of chartContainers) {
        try {
            const dataUrl = await toPng(chartContainer);
            const key = chartContainer.ariaLabel;
            snapShots[key] = dataUrl;
        } catch (err) {
            console.error("Something went wrong with capturing snapshot!", err);
        }
    }
    localStorage.removeItem("isDownloadingCharts");
    return snapShots;
};

// Accept numeric values and numeric strings (including 0)
export const isNumeric = (value) =>
    value !== null && value !== undefined && !Number.isNaN(Number(value));
